import { PubSubService } from '@ohif/core';
import {
  AssessmentSubmission,
  FindingAssessment,
  fetchReportAssessments,
  submitReportAssessments,
} from '../clients/viewer';

/**
 * A server row plus the one fact the UI needs that no payload carries: whether this
 * client has submitted it. `isSubmitted` is either recorded here, because a POST
 * carrying the row returned 2xx, or derived from an echo that is complete for its mode.
 *
 * The union keeps { isSubmitted: true, isDetectionCorrect: undefined } — a row that is
 * required, unsubmittable and locked at once — from being constructible. What it cannot
 * express is that a submitted findings row also carries a classification verdict, since
 * the no-findings row legitimately has none; toTracked and submittableRows hold that.
 */
export interface PendingAssessment extends FindingAssessment {
  isSubmitted: false;
}

export interface SubmittedAssessment extends FindingAssessment {
  isSubmitted: true;
  isDetectionCorrect: boolean;
}

export type TrackedAssessment = PendingAssessment | SubmittedAssessment;

/**
 * A Pending row confirmed complete for its mode. Naming it lets submittableRows' filter
 * carry that guarantee in the type, so recordSubmission can build a SubmittedAssessment
 * from a captured row without an `as` cast.
 */
type SubmittableAssessment = PendingAssessment & { isDetectionCorrect: boolean };

export interface ReportAssessmentStudyState {
  assessments: readonly TrackedAssessment[];
  assessmentRequired: boolean;
  isReportGenerated: boolean;
  /**
   * Kept rather than only tested, because it is the merge's precondition: an index
   * identifies a row only within one report, and a local submission must not survive
   * into the run that replaced it.
   */
  reportId?: string;
}

/**
 * One shared frozen instance, returned for every untracked study. develop returned a
 * fresh object from getInitialState() on each call, which is why getAssessments() could
 * hand back a cached-but-empty array and never refetch (§6 defect 12).
 */
const UNTRACKED: ReportAssessmentStudyState = Object.freeze({
  assessments: Object.freeze([]),
  assessmentRequired: false,
  isReportGenerated: false,
});

const EVENTS = {
  ASSESSMENT_UPDATED: 'event::sycai:ReportAssessmentService:assessmentUpdated',
  ASSESSMENT_SUBMITTED: 'event::sycai:ReportAssessmentService:assessmentSubmitted',
} as const;

/** One payload shape per event, for the same reason as DiagnosisService's. */
export type ReportAssessmentEventPayloads = {
  [EVENTS.ASSESSMENT_UPDATED]: { studyInstanceUid: string };
  [EVENTS.ASSESSMENT_SUBMITTED]: { studyInstanceUid: string };
};

/**
 * develop declared five events here. ASSESSMENT_COMPLETE is redundant once the state
 * projection is the single source of truth (D13), and ASSESSMENT_REVOKED and
 * ASSESSMENTS_FETCHED had zero subscribers. Two survive, and both are consumed by
 * useReportAssessment.
 *
 * develop also shipped a whole second revoke path here — revoke(), the api client's
 * revokeAssessment(), and ASSESSMENT_REVOKED — hitting the same endpoint as
 * DiagnosisService.revoke() with zero callers. Porting the wrong one produces a revoke
 * that compiles, sends the right request, and is wired to nothing. Only
 * DiagnosisService's is reachable, and only it is replayed (§4.1).
 */
export class ReportAssessmentService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'reportAssessmentService',
    create: () => new ReportAssessmentService(),
  };

  private emit<E extends keyof ReportAssessmentEventPayloads>(
    event: E,
    payload: ReportAssessmentEventPayloads[E]
  ): void {
    this._broadcastEvent(event, payload);
  }

  private studies = new Map<string, ReportAssessmentStudyState>();

  constructor() {
    super(EVENTS);
  }

  public getStudy(studyInstanceUid: string): ReportAssessmentStudyState {
    return this.studies.get(studyInstanceUid) ?? UNTRACKED;
  }

  /**
   * Eviction belongs to the mode lifecycle, not to a component's cleanup.
   *
   * This drops the local submission record too, so against a server that never echoes a
   * submission the protection lasts one mode entry: leave such a study and come back and
   * it asks to be assessed again. Deliberately not carried further — what the client may
   * remember that the backend does not is a product question, not a caching one.
   */
  public onModeExit(): void {
    this.studies.clear();
  }

  public isNoFindingsMode(studyInstanceUid: string): boolean {
    return isNoFindings(this.getStudy(studyInstanceUid).assessments);
  }

  /**
   * Always fetches: develop returned the cache whenever an entry existed, including one
   * created empty, so a study could be stuck reporting no findings for the session.
   *
   * Merges rather than replaces, which is what makes a locally recorded submission
   * survive a server that echoes it partially or not at all. The merge applies only when
   * the response describes the same report — same reportId, same row count. Anything
   * else takes the server's rows whole. Per the stub's contract that is also how a
   * revoke clears a study instead of resurrecting its old verdicts — unverified: appears
   * to assume the real API agrees; the stub's README marks that behaviour `[guess]`.
   */
  public async refreshStudy(studyInstanceUid: string): Promise<ReportAssessmentStudyState> {
    const { reportId, findingAssessments } = await fetchReportAssessments(studyInstanceUid);

    const incoming = fromServer(findingAssessments);
    const previous = this.studies.get(studyInstanceUid);

    const isSameReport =
      previous !== undefined &&
      previous.reportId === reportId &&
      previous.assessments.length === incoming.length;

    const assessments = isSameReport ? mergeAssessments(previous.assessments, incoming) : incoming;

    const next: ReportAssessmentStudyState = {
      assessments,
      assessmentRequired: isAssessmentRequired(assessments),
      // isReportGenerated = !!reportId (report-assessment.service.ts:132). No report
      // means the key is absent, per the stub's contract.
      isReportGenerated: Boolean(reportId),
      reportId,
    };

    this.studies.set(studyInstanceUid, next);
    return next;
  }

  /**
   * A local verdict, not yet sent. Submitted rows are immutable, and the dialog disables
   * their toggles to match.
   *
   * The row is addressed by its index in this study's array, not by findingId: the index
   * cannot collide, and this service owns the array it indexes. That is defence against a
   * contract violation rather than an expected shape — the contract reserves
   * `findingId: null` for the no-findings row, so a real finding should always carry an
   * id — but two rows carrying none would match each other, recording a clinical verdict
   * against findings nobody assessed.
   */
  public setVerdict(
    studyInstanceUid: string,
    index: number,
    field: 'isClassificationCorrect' | 'isDetectionCorrect',
    value: boolean | undefined
  ): void {
    const current = this.getStudy(studyInstanceUid);

    const assessments = current.assessments.map((assessment, row) =>
      row === index && !assessment.isSubmitted
        ? ({ ...assessment, [field]: value } as PendingAssessment)
        : assessment
    );

    this.studies.set(studyInstanceUid, { ...current, assessments });
    this.emit(EVENTS.ASSESSMENT_UPDATED, { studyInstanceUid });
  }

  /**
   * The rows a submit would send, each paired with the index that identifies it. One
   * predicate, shared by the wire format and by the local submission record, so "what we
   * sent" and "what we mark submitted" cannot drift apart.
   */
  private submittableRows(
    studyInstanceUid: string
  ): Array<{ index: number; row: SubmittableAssessment }> {
    return submittableRowsOf(this.getStudy(studyInstanceUid).assessments);
  }

  /**
   * What would go over the wire right now, and so also the submit button's enablement
   * test. The asymmetry between the two shapes is develop's: a no-findings study sends
   * one { findingId: null, isDetectionCorrect } with no name.
   */
  public getSubmittable(studyInstanceUid: string): AssessmentSubmission[] {
    const noFindings = this.isNoFindingsMode(studyInstanceUid);

    return this.submittableRows(studyInstanceUid).map(({ row }) =>
      noFindings
        ? { findingId: null, isDetectionCorrect: row.isDetectionCorrect }
        : {
            findingId: row.findingId ?? null,
            name: row.name,
            isClassificationCorrect: row.isClassificationCorrect,
            isDetectionCorrect: row.isDetectionCorrect,
          }
    );
  }

  /**
   * POST, record, then re-read. The 2xx is the submission: recording it locally is what
   * keeps a server that echoes nothing back from re-synthesising an unsubmitted row on
   * the next refresh and sliding the study back to REQUIRES_ASSESSMENT.
   *
   * The rows are captured — index and verdicts together — before the await, so the
   * record can never depend on what `this.studies` holds once the POST lands:
   * recordSubmission writes exactly what was captured, not a fresh read by index.
   * Today nothing can touch these rows while the request is out — DiagnosisButton
   * unmounts this dialog the instant submitAssessment's optimistic PROCESSING lands,
   * before the POST is even issued — but that guarantee belongs to the button, not to
   * this method, and the capture does not depend on it.
   */
  public async submitStudy(studyInstanceUid: string): Promise<void> {
    const rows = this.submittableRows(studyInstanceUid);
    const submission = this.getSubmittable(studyInstanceUid);

    if (submission.length === 0) {
      return;
    }

    await submitReportAssessments(studyInstanceUid, submission);

    this.recordSubmission(studyInstanceUid, rows);

    await this.refreshStudy(studyInstanceUid);

    this.emit(EVENTS.ASSESSMENT_SUBMITTED, { studyInstanceUid });
  }

  /**
   * These rows were sent and accepted, so no later echo may downgrade them; the merge in
   * refreshStudy is what enforces that. assessmentRequired is rebuilt here rather than
   * left to the read-back, so the state is already true if the read-back fails.
   */
  private recordSubmission(
    studyInstanceUid: string,
    submitted: Array<{ index: number; row: SubmittableAssessment }>
  ): void {
    const current = this.getStudy(studyInstanceUid);
    const sent = new Map(submitted.map(({ index, row }) => [index, row]));

    const assessments = current.assessments.map((row, index): TrackedAssessment => {
      const captured = sent.get(index);
      return captured ? { ...captured, isSubmitted: true } : row;
    });

    this.studies.set(studyInstanceUid, {
      ...current,
      assessments,
      assessmentRequired: isAssessmentRequired(assessments),
    });
  }
}

/** See DiagnosisService for why this is a declaration merge rather than a `declare` class field, and why it is an intersection. */
export interface ReportAssessmentService {
  subscribe: (<E extends keyof ReportAssessmentEventPayloads>(
    eventName: E,
    callback: (data: ReportAssessmentEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}

/**
 * The client's view of a response. An empty findingAssessments array means the report
 * has no findings, and the single row the no-findings dialog renders is synthesised
 * here — so what this returns is never empty, and the [0] reads elsewhere need no guard.
 */
function fromServer(findingAssessments: FindingAssessment[]): TrackedAssessment[] {
  if (findingAssessments.length === 0) {
    return [{ findingId: null, isSubmitted: false }];
  }

  const noFindings = isNoFindings(findingAssessments);

  return findingAssessments.map(row => toTracked(row, noFindings));
}

/**
 * Complete for its mode, not "either verdict is present". A row echoed with one of the
 * two verdicts satisfies the looser test and comes back locked: still required, because a
 * verdict is missing; unsubmittable, because the submit filter skips submitted rows; and
 * unanswerable, because setVerdict refuses to change one. Here it stays Pending, so the
 * reviewer can finish it.
 */
function toTracked(row: FindingAssessment, noFindings: boolean): TrackedAssessment {
  const { isClassificationCorrect, isDetectionCorrect } = row;

  // == null, deliberately: a backend that serialises an absent verdict as `null`
  // must be treated the same as one that omits the key.
  if (isDetectionCorrect == null) {
    return { ...row, isSubmitted: false };
  }

  if (!noFindings && isClassificationCorrect == null) {
    return { ...row, isSubmitted: false };
  }

  return { ...row, isSubmitted: true, isDetectionCorrect };
}

/**
 * Pure, so the service and the dialog answer "what would a submit send?" from the same
 * code — the service by study UID, the dialog from the rows it is already subscribed to.
 */
function submittableRowsOf(
  assessments: readonly TrackedAssessment[]
): Array<{ index: number; row: SubmittableAssessment }> {
  const noFindings = isNoFindings(assessments);

  return assessments
    .map((row, index) => ({ index, row }))
    .filter((entry): entry is { index: number; row: SubmittableAssessment } => {
      const { row } = entry;

      // == null, deliberately: a null-echoed verdict is absent, the same as a
      // missing key (toTracked, withEcho).
      if (row.isSubmitted || row.isDetectionCorrect == null) {
        return false;
      }

      // A no-findings study answers one question, so detection alone completes it.
      return noFindings || row.isClassificationCorrect != null;
    });
}

/** The Submit button's enablement test, answered from rows rather than from the Map. */
export function hasSubmittableRows(rows: readonly TrackedAssessment[]): boolean {
  return submittableRowsOf(rows).length > 0;
}

/**
 * The one no-findings test, shared by every caller so they agree by construction. Two
 * copies of it disagreed on a report whose first row had no findingId, which left Submit
 * disabled with the study parked in REQUIRES_ASSESSMENT for the session.
 *
 * Typed structurally so it can be asked of a server row and a tracked one alike, and
 * exported so the dialog can ask it of the rows it already holds. Asking the SERVICE
 * during render read the Map directly, independent of `rows` — which the dialog updates
 * only on ASSESSMENT_UPDATED/ASSESSMENT_SUBMITTED — so within a single render the two
 * could disagree, even though the old code corrected itself on the next unrelated
 * re-render. Deriving from `rows` here buys consistency within a render, not freshness:
 * `rows` can still lag the service (see useReportAssessment's comment on refreshStudy).
 */
export function isNoFindings(rows: readonly { findingId?: string | null }[]): boolean {
  return rows.length === 1 && !rows[0].findingId;
}

/**
 * `assessmentRequired` is entirely client-derived; no payload carries it. One clause is
 * enough because a row is Submitted only when this client sent it or the server echoed it
 * complete, so "not submitted" is exactly "still needs an answer".
 */
function isAssessmentRequired(assessments: readonly TrackedAssessment[]): boolean {
  return assessments.some(assessment => !assessment.isSubmitted);
}

/**
 * Index-wise, and only reached when the two arrays describe the same report. Assumes row
 * order is stable within a report, the same assumption every other index-addressed
 * operation in this file makes from setVerdict on: an echo's row at position i is read as
 * an update to the locally recorded row at position i, not merely a coincidence of count.
 *
 * A locally recorded submission is a fact about what this client sent and the server
 * accepted, so an echo may correct its contents but never downgrade it to Pending. The
 * asymmetry that buys: a server that legitimately dropped a verdict cannot say so through
 * this path. A revoke still can, per the stub's contract, which clears the reportId and
 * lets the state projection read IDLE whatever these rows say — unverified: appears to
 * assume the real API agrees; the stub's README marks that behaviour `[guess]`.
 */
function mergeAssessments(
  previous: readonly TrackedAssessment[],
  incoming: readonly TrackedAssessment[]
): TrackedAssessment[] {
  return incoming.map((row, index) => {
    const local = previous[index];

    return local?.isSubmitted ? withEcho(local, row) : row;
  });
}

/**
 * Only keys the server actually sent. `!= null` catches both spellings of absent —
 * undefined and null — either of which would otherwise overwrite a verdict this client
 * knows it sent and the server accepted. `findingId` is never one of those keys: `merged`
 * starts from `local`'s spread and nothing here reassigns it, so the echo's own value,
 * whatever it is, is silently discarded in favour of the locally recorded id.
 */
function withEcho(local: SubmittedAssessment, echo: TrackedAssessment): SubmittedAssessment {
  const merged: SubmittedAssessment = { ...local };

  if (echo.name != null) {
    merged.name = echo.name;
  }

  if (echo.isClassificationCorrect != null) {
    merged.isClassificationCorrect = echo.isClassificationCorrect;
  }

  if (echo.isDetectionCorrect != null) {
    merged.isDetectionCorrect = echo.isDetectionCorrect;
  }

  return merged;
}
